import React, { cloneElement, ReactElement, useState, ReactNode, ComponentProps } from 'react';
import { createPortal } from 'react-dom';
import { usePopper } from 'react-popper';
import useToggle from 'react-use/lib/useToggle';
import { formatText } from '../../utils/format/formatText';
import { IconHelp } from '../icon/IconHelp';
import { Box } from '../layout/Box';
import { Flex } from '../layout/Flex';
import { Typography } from '../typography/Typography';
import { ReactComponent as TooltipArrow } from './resources/tooltipArrow.svg';
import type { Options } from '@popperjs/core';

interface TooltipProps {
    trigger: ReactElement;
    children: ReactNode;
    options?: Omit<Partial<Options>, 'modifiers'>;
}

export const Tooltip = (props: TooltipProps) => {
    const { trigger, options = { placement: 'bottom-start' }, children, ...other } = props;
    const [open, toggleOpen] = useToggle(false);
    const [referenceElement, setReferenceElement] = useState<HTMLElement | null>(null);
    const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null);
    const { styles, attributes, update, state } = usePopper(
        referenceElement,
        popperElement,
        options,
    );

    return (
        <>
            {cloneElement(trigger, {
                ref: setReferenceElement,
                onMouseEnter: () => {
                    toggleOpen(true);
                    update?.();
                },
                onMouseLeave: () => {
                    toggleOpen(false);
                    update?.();
                },
            })}
            {open &&
                createPortal(
                    <Box
                        ref={setPopperElement}
                        zIndex={9999}
                        style={styles.popper}
                        {...attributes.popper}
                    >
                        <Box
                            style={styles.offset}
                            display={open ? 'block' : 'none'}
                            my={1}
                            mx={1}
                            maxWidth={300}
                            position="relative"
                            background="var(--background-invert)"
                            px={2}
                            py={1}
                            borderRadius={2}
                        >
                            {(state?.modifiersData?.preventOverflow?.x ?? 0) === 0 && (
                                <Box
                                    position="absolute"
                                    left={0}
                                    style={
                                        state?.placement?.startsWith('top')
                                            ? {
                                                  transform: 'scaleY(-1)',
                                                  bottom: '-7px',
                                              }
                                            : { top: '-7px' }
                                    }
                                >
                                    <TooltipArrow fill="var(--background-invert)" width={15} />
                                </Box>
                            )}
                            {children}
                        </Box>
                    </Box>,
                    document.body,
                )}
        </>
    );
};

interface WithHintProps extends ComponentProps<typeof Flex> {
    message: string;
    children?: ReactNode;
}

export const WithHint = ({ message, children, ...other }: WithHintProps) => (
    <Flex inline direction="row" alignItems="center" spacing={0.5} {...other}>
        {children}
        <Tooltip trigger={<IconHelp color="var(--color-primary)" cursor="help" size={16} />}>
            <Typography variant="caption" color="var(--color-invert)">
                {formatText(message)}
            </Typography>
        </Tooltip>
    </Flex>
);
